import { IApiBooking, IApiEvent, IBooking, IEvent } from '@travelnest/typewriter';
import { fromFloyd, toFloyd } from '../entities/pricing/helpers';
import { stripTimezone } from '../view/helpers/date_helpers';
import { IConfiguredHttp } from './http';

export type IAvailability = Pick<IEvent, 'start_on' | 'end_on' | 'property_id'>;

export interface IAvailabilityApi {
	getByPropertyId: (id: string) => Promise<IAvailability[]>;
	createEvent: (event: IEvent) => Promise<IEvent[]>;
	updateEvent: (event: IEvent) => Promise<IEvent>;
	deleteEvent: (event: IEvent) => Promise<IEvent>;
	createEventWithBooking: (event: IEvent) => Promise<IEvent>;
	getBookingByEventId: (id: string) => Promise<IEvent>;
	getBookingsBySearchText: (text: string) => Promise<IEvent[]>;
}

const receiveBooking = (booking: IApiBooking): IBooking => ({
	...booking,
	value: {
		...booking.value,
		amount: fromFloyd(booking.value.amount)
	},
	payment_date: new Date(booking.payment_date)
});

const receiveEvent = (period: IApiEvent): IEvent => ({
	...period,
	booking: period.booking ? receiveBooking(period.booking) : (period.booking as any),
	start_on: new Date(period.start_on),
	end_on: new Date(period.end_on)
});

const sendBooking = (booking: IBooking): IApiBooking => ({
	...booking,
	payment_date: stripTimezone(booking.payment_date.toISOString()),
	value: {
		...booking.value,
		amount: toFloyd(booking.value.amount)
	}
});

const sendEvent = (event: IEvent): IApiEvent => ({
	...event,
	start_on: event.start_on ? stripTimezone(event.start_on.toISOString()) : (event.start_on as any),
	end_on: event.end_on ? stripTimezone(event.end_on.toISOString()) : (event.end_on as any),
	booking: event.booking ? sendBooking(event.booking) : (event.booking as any)
});

const availabilityApiFactory = (http: IConfiguredHttp, prefix = ''): IAvailabilityApi => ({
	getByPropertyId(id) {
		return http.get(`${prefix}/property/${id}`).then((data) => data.map(receiveEvent));
	},
	createEvent(event: IEvent) {
		return http.post(`${prefix}/events/property/${event.property_id}`, sendEvent(event)).then((data) => data.map(receiveEvent));
	},
	updateEvent(event: IEvent) {
		return http.put(`${prefix}/property/${event.property_id}/events/${event.id}`, sendEvent(event));
	},
	deleteEvent(event: IEvent) {
		return http.delete(`${prefix}/property/${event.property_id}/events/${event.id}`);
	},
	createEventWithBooking(event: IEvent) {
		return http.post(`${prefix}/booking`, sendEvent(event)).then((data) => receiveEvent(data));
	},
	getBookingByEventId(id: string) {
		return http.get(`${prefix}/events/${id}`).then((data) => data);
	},
	getBookingsBySearchText(text: string) {
		return http.get(`${prefix}/v2/booking/search?q=${text}`).then((data) => data);
	}
});

export default availabilityApiFactory;
